import { instance } from '~/utils/api/api';
import dayjs, { Dayjs } from 'dayjs';
import { BUTTON_STYLES, Button } from '~/ui';
import styles from './XlsReportServicesPopoverContent.module.scss';
import { useContext, useState } from 'react';
import { PopoverContext } from '~/components/XlsUploadFilePopover/XlsUploadFilePopover';
import { downloadFile } from '~/utils/downloadFile';
import { useTranslation } from 'react-i18next';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';
import { useToastError } from '~/utils/useToastError';
import CustomQuerySelect from '~/components/Shared/CustomQuerySelect/CustomQuerySelect';
import { useDebounce } from '@uidotdev/usehooks';

interface IAccommodation {
  id: number;
  name: string;
}

const XlsReportServicesPopoverContent = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [isAllAccommodationChecked, setIsAllAccommodationChecked] =
    useState<boolean>(false);
  const { setIsPopoverVisible } = useContext(PopoverContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [entertainments, setEntertainments] = useState<IAccommodation[]>([]);
  const debouncedSearchTerm = useDebounce('', 500);
  const { t } = useTranslation();
  const toastError = useToastError();

  const onClosePopup = () => {
    setStartDate(null);
    setEndDate(null);
    setIsPopoverVisible(false);
  };

  const params = {
    sort: '',
    perPage: 100,
    field: '',
    query: debouncedSearchTerm,
    page: 1,
  };

  const downloadFileReport = async () => {
    try {
      setIsLoading(true);

      const timestamp = Date.now();
      const entertainment_object_ids = entertainments.map(item => item.id);

      const response = await instance.get(
        `admin/entertainment-export?t=${timestamp}`,
        {
          params: {
            dates: [
              startDate?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
              endDate?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            ],
            entertainment_object_ids,
          },
          responseType: 'blob',
        }
      );
      downloadFile(response.data, 'text/xlsx', `analytics-service.xlsx`);
    } catch (error) {
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const disabledSendReport = !startDate || !endDate;

  const disabledDate = (current: Dayjs) => {
    return current && current > dayjs().startOf('day');
  };

  return (
    <div className={styles.formWrapper}>
      <span className={styles.formTitle}>{t('analytics_on_services')}</span>
      <div className={styles.radioBtnTypeReportWrapper}>
        <CustomQuerySelect
          label={t('services')}
          query="admin/entertainment-objects"
          queryKey={['entertainment-objects', params]}
          placeholder={t('services')}
          params={params}
          value={entertainments}
          onChange={value => {
            if (Array.isArray(value)) {
              setEntertainments(value);
            }
          }}
          required
          disabled={isAllAccommodationChecked}
          multiselect
          isSearchEnabled
          hasCheckbox={true}
          checkboxLabel={t('for_all_services')}
          setIsCheckedAll={value => setIsAllAccommodationChecked(value)}
        />
      </div>
      <div className={styles.dateWrapper}>
        <div className={styles.labelWrapper}>
          <span className={styles.labelText}>
            {t('date')}
            <span className={styles.labelStar}>*</span>
          </span>
          <CustomDatePicker
            value={[startDate, endDate]}
            showPresets={true}
            disabledDate={disabledDate}
            onChange={(name, value) => {
              if (name === 'start_date') {
                setStartDate(value);
              } else if (name === 'end_date') {
                setEndDate(value);
              }
            }}
          />
        </div>
      </div>
      <div className={styles.btnWrapper}>
        <Button
          onClick={onClosePopup}
          className={styles.contentBtn}
          text={t('cancel')}
          disabled={false}
          buttonStyle={BUTTON_STYLES.EMPTY}
        />
        <Button
          onClick={downloadFileReport}
          type="submit"
          text={t('download_report')}
          className={styles.sendBtn}
          disabled={disabledSendReport || isLoading}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default XlsReportServicesPopoverContent;
